// ZodiacCrush — Engine Types & Constants

export const SIGNS = [
  'aries',
  'tauro',
  'geminis',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'escorpio',
  'sagitario',
  'capricornio',
  'acuario',
  'piscis',
];

export const SIGN_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagitario: 'fire',

  tauro: 'earth',
  virgo: 'earth',
  capricornio: 'earth',

  geminis: 'air',
  libra: 'air',
  acuario: 'air',

  cancer: 'water',
  escorpio: 'water',
  piscis: 'water',
};

export const ELEMENTS = ['fire', 'water', 'air', 'earth'];

export const ELEMENT_COLOR = {
  fire: '#FF6A00',
  earth: '#8B6B3E',
  air: '#9FD4FF',
  water: '#3FA9F5',
};

export const PLANETS = [
  'sun',
  'moon',
  'mercury',
  'venus',
  'mars',
  'jupiter',
  'saturn',
  'uranus',
  'neptune',
  'pluto',
];

export const TILE_KIND = {
  SIGN: 'SIGN',
  ELEMENT: 'ELEMENT',
  PLANET: 'PLANET',
};

export const TILE_RARITY = {
  NORMAL: 'normal',
  RARE: 'rare',
  EPIC: 'epic',
};

// Default board config (used by engine/board.js)
export const DEFAULT_BOARD = {
  rows: 8,
  cols: 8,
};

// Power-ups (Candy Crush style)
export const TILE_SPECIAL = {
  NONE: null,

  // Clears
  STRIPE_H: 'stripe_h', // clears row
  STRIPE_V: 'stripe_v', // clears column

  // Specials
  // NOTE: keep STAR for backward compatibility (existing engine/UI uses it)
  STAR: 'star',       // color bomb (legacy name)
  COLOR_BOMB: 'star', // alias of STAR (new semantic name)
  BOMB: 'bomb',       // wrapped bomb (clears area)
};

export const SPECIAL_TYPES = [
  TILE_SPECIAL.STRIPE_H,
  TILE_SPECIAL.STRIPE_V,
  TILE_SPECIAL.STAR, // includes COLOR_BOMB alias
  TILE_SPECIAL.BOMB,
];

export const SIGN_EMOJI = {
  aries: '♈',
  tauro: '♉',
  geminis: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  escorpio: '♏',
  sagitario: '♐',
  capricornio: '♑',
  acuario: '♒',
  piscis: '♓',
};

export const ELEMENT_EMOJI = {
  fire: '🔥',
  water: '💧',
  air: '🌪️',
  earth: '🌿',
};

export const PLANET_EMOJI = {
  sun: '☀️',
  moon: '🌙',
  mercury: '☿️',
  venus: '♀️',
  mars: '♂️',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
};

export function tileLabel(tile) {
  if (!tile) return '';

  // Power-up visuals override base tile
  if (tile.special === TILE_SPECIAL.STRIPE_H) return '➖';
  if (tile.special === TILE_SPECIAL.STRIPE_V) return '➕';
  // STAR / COLOR_BOMB share the same internal value ('star')
  if (tile.special === TILE_SPECIAL.STAR || tile.special === TILE_SPECIAL.COLOR_BOMB) return '✨';
  if (tile.special === TILE_SPECIAL.BOMB) return '💣';

  if (tile.kind === TILE_KIND.SIGN) return SIGN_EMOJI[tile.key] || '✦';
  if (tile.kind === TILE_KIND.ELEMENT) return ELEMENT_EMOJI[tile.key] || '✦';
  if (tile.kind === TILE_KIND.PLANET) return PLANET_EMOJI[tile.key] || '✦';
  return '✦';
}

export function tileElement(tile) {
  if (!tile) return null;
  // Color bomb has no element; bombs keep their element (so they can be matched/targeted if needed)
  if (tile.special === TILE_SPECIAL.STAR || tile.special === TILE_SPECIAL.COLOR_BOMB) return null;
  if (tile.element) return tile.element;
  if (tile.kind === TILE_KIND.ELEMENT) return tile.key;
  if (tile.kind === TILE_KIND.SIGN) return SIGN_ELEMENT[tile.key] || null;
  return null; // planets default to neutral
}

export function isSpecialTile(tile) {
  return !!tile?.special && tile.special !== TILE_SPECIAL.NONE;
}

export function isStripe(tile) {
  return tile?.special === TILE_SPECIAL.STRIPE_H || tile?.special === TILE_SPECIAL.STRIPE_V;
}

export function isStar(tile) {
  // STAR and COLOR_BOMB share the same value, keep both for clarity
  return tile?.special === TILE_SPECIAL.STAR || tile?.special === TILE_SPECIAL.COLOR_BOMB;
}

export function isColorBomb(tile) {
  return isStar(tile);
}

export function isBomb(tile) {
  return tile?.special === TILE_SPECIAL.BOMB;
}

export function tileDisplayName(tile) {
  if (!tile) return '';
  if (tile.kind === TILE_KIND.SIGN) return tile.key;
  if (tile.kind === TILE_KIND.ELEMENT) return tile.key;
  if (tile.kind === TILE_KIND.PLANET) return tile.key;
  return '';
}